import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

const styles = {
  global: `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; background: #1a1a2e; min-height: 100vh; color: white; }
    video { border-radius: 12px; background: #0f0f1a; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  `,
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  logo: {
    fontSize: '24px',
    fontWeight: '600',
    color: 'white',
  },
  timer: {
    background: 'rgba(255,255,255,0.1)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  liveIndicator: {
    width: '10px',
    height: '10px',
    background: '#e74c3c',
    borderRadius: '50%',
    animation: 'pulse 1s infinite',
  },
  videoContainer: {
    flex: 1,
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
  },
  videoWrapper: {
    flex: 1,
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#0f0f1a',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '16px',
  },
  videoLabel: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    background: 'rgba(0,0,0,0.6)',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
  },
  localVideoWrapper: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    width: '200px',
    height: '150px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '3px solid rgba(255,255,255,0.2)',
    background: '#0f0f1a',
  },
  localVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)',
  },
  noVideo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px',
  },
  noVideoIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '20px',
  },
  controlButton: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  muteButton: {
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
  },
  muteButtonActive: {
    background: '#e74c3c',
    color: 'white',
  },
  videoButton: {
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
  },
  videoButtonActive: {
    background: '#e74c3c',
    color: 'white',
  },
  endButton: {
    background: '#e74c3c',
    color: 'white',
    width: '80px',
  },
  statusBar: {
    textAlign: 'center',
    padding: '12px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  statusText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
  },
  matchInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  matchAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '600',
  },
  matchName: {
    fontSize: '18px',
    fontWeight: '600',
  },
  matchRole: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
  },
  errorBox: {
    background: 'rgba(231, 76, 60, 0.2)',
    border: '1px solid rgba(231, 76, 60, 0.3)',
    borderRadius: '12px',
    padding: '16px',
    margin: '20px 0',
    textAlign: 'center',
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '14px',
  },
  retryButton: {
    marginTop: '12px',
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
};

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

function InterviewPage({ user, setUser }) {
  const navigate = useNavigate();
  
  // All mutable state in refs to avoid stale closures
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // Store interview data in refs so handlers always have current values
  const interviewDataRef = useRef(null);
  const isInitiatorRef = useRef(false);
  const roomIdRef = useRef(null);
  const peerIdRef = useRef(null);
  
  // Guards
  const isInitialized = useRef(false);
  const hasCreatedOffer = useRef(false);
  const hasReceivedOffer = useRef(false);
  const hasReceivedAnswer = useRef(false);
  const iceCandidatesQueue = useRef([]);

  // React state for UI rendering only
  const [interviewData, setInterviewData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [mediaError, setMediaError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [hasLocalVideo, setHasLocalVideo] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  // Cleanup function
  const cleanup = useCallback(() => {
    console.log('Cleaning up...');
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    // Reset guards
    hasCreatedOffer.current = false;
    hasReceivedOffer.current = false;
    hasReceivedAnswer.current = false;
    iceCandidatesQueue.current = [];
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (isInitialized.current) {
      console.log('Already initialized, skipping...');
      return;
    }
    
    const data = sessionStorage.getItem('interviewData');
    if (!data) {
      navigate('/home');
      return;
    }

    const parsed = JSON.parse(data);
    
    // Set the initialization flag FIRST
    isInitialized.current = true;
    
    // Store in refs for use in handlers (avoids stale closures)
    interviewDataRef.current = parsed;
    isInitiatorRef.current = parsed.isInitiator;
    roomIdRef.current = parsed.roomId;
    peerIdRef.current = parsed.peerId;
    
    // Store in state for UI rendering
    setInterviewData(parsed);
    
    console.log('=== INTERVIEW INIT ===');
    console.log('isInitiator:', parsed.isInitiator);
    console.log('roomId:', parsed.roomId);
    console.log('peerId:', parsed.peerId);
    
    initializeCall();

    // Duration timer
    const durationInterval = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(durationInterval);
      cleanup();
      isInitialized.current = false;
    };
  }, [navigate, cleanup]);

  const getMediaStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      setHasLocalVideo(true);
      return stream;
    } catch (err) {
      console.log('Failed to get video+audio:', err.message);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true
      });
      setHasLocalVideo(false);
      setMediaError('Camera not available. Using audio only.');
      return stream;
    } catch (err) {
      console.log('Failed to get audio:', err.message);
    }

    setMediaError('Could not access camera or microphone.');
    return null;
  };

  const initializeCall = async () => {
    try {
      setConnectionStatus('connecting');
      
      // Get local media
      const stream = await getMediaStream();
      if (stream) {
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      }

      // Create peer connection (reads roomId from ref)
      createPeerConnection();

      // Connect socket
      const token = localStorage.getItem('token');
      socketRef.current = io(SOCKET_URL, { auth: { token } });

      socketRef.current.on('connect', () => {
        // Read current values from refs, not closures
        const roomId = roomIdRef.current;
        const isInitiator = isInitiatorRef.current;
        
        console.log('Socket connected, joining room:', roomId);
        socketRef.current.emit('join-room', { roomId });
        
        if (isInitiator && !hasCreatedOffer.current) {
          console.log('I am initiator, will create offer in 1s...');
          setTimeout(() => {
            if (!hasCreatedOffer.current) {
              createOffer();
            }
          }, 1000);
        }
      });

      socketRef.current.on('user-joined', ({ userId }) => {
        console.log('User joined room:', userId);
      });

      socketRef.current.on('webrtc-offer', async ({ userId, offer }) => {
        // Read from ref, not closure
        if (isInitiatorRef.current) {
          console.log('I am initiator, ignoring offer');
          return;
        }
        
        if (hasReceivedOffer.current) {
          console.log('Already received offer, ignoring');
          return;
        }
        
        hasReceivedOffer.current = true;
        console.log('Processing offer from:', userId);
        await handleOffer(offer);
      });

      socketRef.current.on('webrtc-answer', async ({ userId, answer }) => {
        // Read from ref, not closure
        if (!isInitiatorRef.current) {
          console.log('Not initiator, ignoring answer');
          return;
        }
        
        if (hasReceivedAnswer.current) {
          console.log('Already received answer, ignoring');
          return;
        }
        
        hasReceivedAnswer.current = true;
        console.log('Processing answer from:', userId);
        await handleAnswer(answer);
      });

      socketRef.current.on('webrtc-ice-candidate', async ({ userId, candidate }) => {
        await handleIceCandidate(candidate);
      });

      socketRef.current.on('interview-ended', () => {
        console.log('Interview ended by other party');
        cleanup();
        sessionStorage.removeItem('interviewData');
        navigate('/home');
      });

    } catch (error) {
      console.error('Error initializing call:', error);
      setConnectionStatus('error');
      setMediaError(error.message);
    }
  };

  const createPeerConnection = () => {
    if (peerConnectionRef.current) {
      console.log('Peer connection already exists');
      return;
    }
    
    console.log('Creating peer connection...');
    peerConnectionRef.current = new RTCPeerConnection(ICE_SERVERS);

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Adding local track:', track.kind);
        peerConnectionRef.current.addTrack(track, localStreamRef.current);
      });
    }

    peerConnectionRef.current.ontrack = (event) => {
      console.log('Received remote track:', event.track.kind);
      console.log('Remote streams:', event.streams);
      
      if (event.streams && event.streams[0]) {
        // Store the stream in ref
        remoteStreamRef.current = event.streams[0];
        setHasRemoteVideo(true);
        
        // Try to set on video element if it exists
        if (remoteVideoRef.current) {
          console.log('Setting remote video srcObject immediately');
          remoteVideoRef.current.srcObject = event.streams[0];
          remoteVideoRef.current.play().catch(err => {
            console.log('Remote video play error:', err);
          });
        } else {
          console.log('remoteVideoRef not ready, stream stored in ref');
        }
      }
      setConnectionStatus('connected');
    };

    peerConnectionRef.current.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        // Read roomId from ref
        socketRef.current.emit('webrtc-ice-candidate', {
          roomId: roomIdRef.current,
          candidate: event.candidate
        });
      }
    };

    peerConnectionRef.current.onconnectionstatechange = () => {
      const state = peerConnectionRef.current?.connectionState;
      console.log('Connection state:', state);
      if (state === 'connected') {
        setConnectionStatus('connected');
      } else if (state === 'disconnected' || state === 'failed') {
        setConnectionStatus('disconnected');
      }
    };

    peerConnectionRef.current.oniceconnectionstatechange = () => {
      console.log('ICE state:', peerConnectionRef.current?.iceConnectionState);
    };
  };

  const createOffer = async () => {
    if (hasCreatedOffer.current) {
      console.log('Offer already created');
      return;
    }
    
    hasCreatedOffer.current = true;
    
    try {
      console.log('Creating offer...');
      const offer = await peerConnectionRef.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      await peerConnectionRef.current.setLocalDescription(offer);
      
      // Read roomId from ref
      socketRef.current?.emit('webrtc-offer', { 
        roomId: roomIdRef.current, 
        offer 
      });
      console.log('Offer sent');
    } catch (error) {
      console.error('Error creating offer:', error);
      hasCreatedOffer.current = false;
    }
  };

  const handleOffer = async (offer) => {
    try {
      console.log('Setting remote description from offer...');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Process queued ICE candidates
      while (iceCandidatesQueue.current.length > 0) {
        const candidate = iceCandidatesQueue.current.shift();
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      }
      
      console.log('Creating answer...');
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      
      // Read roomId from ref
      socketRef.current?.emit('webrtc-answer', { 
        roomId: roomIdRef.current, 
        answer 
      });
      console.log('Answer sent');
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };

  const handleAnswer = async (answer) => {
    try {
      console.log('Setting remote description from answer...');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
      
      // Process queued ICE candidates
      while (iceCandidatesQueue.current.length > 0) {
        const candidate = iceCandidatesQueue.current.shift();
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };

  const handleIceCandidate = async (candidate) => {
    try {
      if (peerConnectionRef.current?.remoteDescription) {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        iceCandidatesQueue.current.push(candidate);
      }
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const endCall = async () => {
    // Read peerId from ref
    if (peerIdRef.current) {
      socketRef.current?.emit('end-interview', { peerId: peerIdRef.current });
    }
    cleanup();
    sessionStorage.removeItem('interviewData');
    
    try {
      const response = await fetch('http://localhost:5000/api/auth/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isInterviewing: false, lookingForInterview: false })
      });
      const data = await response.json();
      if (data.success) {
        setUser(data.data.user);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
    
    navigate('/home');
  };

  const retryMedia = async () => {
    setMediaError(null);
    const stream = await getMediaStream();
    if (stream) {
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!interviewData) {
    return null;
  }

  return (
    <>
      <style>{styles.global}</style>

      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logo}>Instaview</div>
          <div style={styles.timer}>
            <div style={styles.liveIndicator}></div>
            {formatDuration(callDuration)}
          </div>
        </div>

        {interviewData.matchedUser && (
          <div style={styles.matchInfo}>
            <div style={styles.matchAvatar}>
              {interviewData.matchedUser.firstName?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={styles.matchName}>
                {interviewData.matchedUser.firstName} {interviewData.matchedUser.lastName}
              </div>
              <div style={styles.matchRole}>
                {interviewData.matchedUser.role === 'hiring_manager' ? 'Hiring Manager' : 'Job Seeker'}
              </div>
            </div>
          </div>
        )}

        {mediaError && (
          <div style={styles.errorBox}>
            <div style={styles.errorText}>⚠️ {mediaError}</div>
            <button onClick={retryMedia} style={styles.retryButton}>
              Retry Camera/Mic
            </button>
          </div>
        )}

        <div style={styles.statusBar}>
          <span style={styles.statusText}>
            {connectionStatus === 'connecting' && '🟡 Connecting...'}
            {connectionStatus === 'connected' && '🟢 Connected'}
            {connectionStatus === 'disconnected' && '🔴 Disconnected'}
            {connectionStatus === 'error' && '🔴 Connection error'}
          </span>
        </div>

        <div style={styles.videoContainer}>
          <div style={styles.videoWrapper}>
            {hasRemoteVideo ? (
              <video
                ref={(el) => {
                  remoteVideoRef.current = el;
                  // Apply stored stream when element mounts
                  if (el && remoteStreamRef.current && !el.srcObject) {
                    console.log('Applying stored remote stream to video element');
                    el.srcObject = remoteStreamRef.current;
                    el.play().catch(err => console.log('Play error:', err));
                  }
                }}
                autoPlay
                playsInline
                style={styles.video}
              />
            ) : (
              <div style={styles.noVideo}>
                <div style={styles.noVideoIcon}>👤</div>
                <div>Waiting for {interviewData.matchedUser?.firstName}'s video...</div>
              </div>
            )}
            <div style={styles.videoLabel}>
              {interviewData.matchedUser?.firstName || 'Participant'}
            </div>
            
            <div style={styles.localVideoWrapper}>
              {hasLocalVideo && !isVideoOff ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={styles.localVideo}
                />
              ) : (
                <div style={{ ...styles.noVideo, height: '100%' }}>
                  <div style={{ fontSize: '24px' }}>📵</div>
                  <div style={{ fontSize: '11px' }}>Camera off</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.controls}>
          <button
            onClick={toggleMute}
            style={{
              ...styles.controlButton,
              ...(isMuted ? styles.muteButtonActive : styles.muteButton)
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🎤'}
          </button>

          <button
            onClick={toggleVideo}
            style={{
              ...styles.controlButton,
              ...(isVideoOff ? styles.videoButtonActive : styles.videoButton)
            }}
            title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            disabled={!hasLocalVideo}
          >
            {isVideoOff ? '📵' : '📹'}
          </button>

          <button
            onClick={endCall}
            style={{ ...styles.controlButton, ...styles.endButton }}
            title="End call"
          >
            📞
          </button>
        </div>
      </div>
    </>
  );
}

export default InterviewPage;